import { api } from 'constants/constant';
import { getHeaders } from 'auth/auth';
import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';

// Prepayment scenario -> query string. Only set levers travel, so the cache key for
// "no scenario" stays identical to the plain schedule request.
const prepaymentQuery = (prepayment) => {
  const entries = Object.entries(prepayment || {}).filter(
    ([, value]) => value !== null && value !== undefined && value !== '' && Number(value) > 0
  );
  if (!entries.length) return '';
  return `?${new URLSearchParams(entries.map(([k, v]) => [k, String(v)])).toString()}`;
};

export const adminToolsAPI = createApi({
  reducerPath: 'adminToolsAPI',
  tagTypes: ['adminTools'],
  baseQuery: fetchBaseQuery({ baseUrl: api, credentials: 'include' }),
  endpoints: (builder) => ({
    getCurrentOffer: builder.query({
      query: ({ applicationId }) => {
        return {
          url: `b2b/loan/tools/get_current_offer/application/${applicationId}`,
          method: 'GET',
          headers: getHeaders(),
        };
      },
    }),
    compareLoanOfficer: builder.mutation({
      query: ({ data }) => {
        return {
          url: `b2b/loan/tools/compare_offer_by_user_input`,
          method: 'POST',
          body: data,
          headers: getHeaders(),
        };
      },
    }),
    getUsers: builder.query({
      query: () => {
        return {
          url: `b2b/loan/tools/get_users`,
          method: 'POST',
          body: {},
          headers: getHeaders(),
        };
      },
    }),
    getAmortizationSchedule: builder.query({
      query: ({ applicationId, prepayment }) => {
        return {
          url: `b2b/loan/tools/get_amortization_schedule/application/${applicationId}${prepaymentQuery(prepayment)}`,
          method: 'GET',
          headers: getHeaders(),
        };
      },
    }),
    compareOfferDocumentUpload: builder.mutation({
      query: ({ data }) => {
        const headers = getHeaders();
        delete headers['Content-Type'];
        delete headers['content-type'];
        return {
          url: `b2b/loan/tools/compare_offer_document_upload`,
          body: data,
          headers,
          method: 'POST',
        };
      },
    }),
    downloadQuoteApplication: builder.mutation({
      query: ({ data }) => {
        return {
          url: `b2b/loan/tools/download_quote`,
          body: data,
          headers: getHeaders(),
          method: 'POST',
        };
      },
    }),
    getAmortizationScheduleConsumer: builder.query({
      query: ({ applicationId, prepayment }) => {
        return {
          url: `b2c/loan/tools/get_amortization_schedule_consumer/application/${applicationId}${prepaymentQuery(prepayment)}`,
          method: 'GET',
          headers: getHeaders(),
        };
      },
    }),
    getCurrentOfferConsumer: builder.query({
      query: ({ applicationId }) => {
        return {
          url: `b2c/loan/tools/get_current_offer_consumer/application/${applicationId}`,
          method: 'GET',
          headers: getHeaders(),
        };
      },
    }),
    /* Restores the last comparison's INPUTS and recomputes against today's offer,
       so a reload never shows figures pinned to a rate that has since changed. */
    getLatestComparison: builder.query({
      query: ({ applicationId }) => ({
        url: `b2b/loan/tools/latest_comparison/application/${applicationId}`,
        method: 'GET',
        headers: getHeaders(),
      }),
    }),
    getLatestComparisonConsumer: builder.query({
      query: ({ applicationId }) => ({
        url: `b2c/loan/tools/latest_comparison_consumer/application/${applicationId}`,
        method: 'GET',
        headers: getHeaders(),
      }),
    }),
    getConsumerToolApplications: builder.query({
      query: () => {
        return {
          url: `b2c/loan/tools/applications`,
          method: 'GET',
          headers: getHeaders(),
        };
      },
    }),

    compareUserInputConsumer: builder.mutation({
      query: ({ data }) => {
        return {
          url: `b2c/loan/tools/compare_offer_by_user_input_consumer`,
          method: 'POST',
          body: data,
          headers: getHeaders(),
        };
      },
    }),

    consumerOfferDocument: builder.mutation({
      query: ({ data }) => {
        const headers = getHeaders();
        delete headers['Content-Type'];
        delete headers['content-type'];
        return {
          url: `b2c/loan/tools/compare_offer_document_upload_consumer`,
          body: data,
          headers,
          method: 'POST',
        };
      },
    }),
  }),
});

export const {
  useGetCurrentOfferQuery,
  useCompareLoanOfficerMutation,
  useGetUsersQuery,
  useGetAmortizationScheduleQuery,
  useCompareOfferDocumentUploadMutation,
  useDownloadQuoteApplicationMutation,
  useGetAmortizationScheduleConsumerQuery,
  useGetCurrentOfferConsumerQuery,
  useGetLatestComparisonQuery,
  useGetLatestComparisonConsumerQuery,
  useGetConsumerToolApplicationsQuery,
  useCompareUserInputConsumerMutation,
  useConsumerOfferDocumentMutation,
} = adminToolsAPI;
